import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { hideNotification, showNotification } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { cloneDatasets as copyDatasets } from '@mygpt/app';

import { ModelSelect } from '@/components/Create_V2/ModelSelect';
import { useCreateStore } from '@/store/create';
import { useDatasetStore } from '@/store/dataset';

const CloneModalContent = ({ name }: { name: string }) => {
  const { t } = useTranslation();
  const { setEmbeddingModelName } = useCreateStore();

  useEffect(() => {
    setEmbeddingModelName('text-embedding-3-large');
  }, []);

  return (
    <Stack className="py-3">
      <Text size="lg" fw={500}>
        {t('dataset.clone_modal.title', { name: `${name.substring(0, 10)}...` })}
      </Text>
      <ModelSelect />
      <Group gap={10} wrap="nowrap" className="items-start">
        <IconInfoCircle size={18} className="flex-shrink-0 mt-1" />
        <Text size="base">{t('dataset.clone_modal.content_desc')}</Text>
      </Group>
    </Stack>
  );
};

export const CloneNotificationContent = ({ id, name }: { name: string; id: string }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Group justify="space-between" wrap="nowrap" gap={0}>
      <Text size={'sm'}>{t('dataset.cloning_view_tip', { name })}</Text>

      <Button
        variant="outline"
        size="xs"
        color="green"
        onClick={() => {
          navigate(`/datasets`);
          hideNotification(id);
        }}
        className="flex-shrink-0"
      >
        {t('dataset.view_btn')}
      </Button>
    </Group>
  );
};

export const CloneModal = ({ callback, from = '' }: { callback: (from?: string) => void; from?: string }) => {
  const { t } = useTranslation();
  const { cloneTarget, setCloneTarget } = useDatasetStore();
  const [cloneLoading, setCloneLoading] = useState(false);
  const { embeddingModelName } = useCreateStore();

  if (!cloneTarget) return null;

  const { id, name } = cloneTarget;

  const handleModalClose = () => {
    setCloneTarget(null);
  };

  const cloneDatasets = () => {
    setCloneLoading(true);
    copyDatasets({ id, target_embedding_model: embeddingModelName })
      .then(() => {
        if (['nav', 'linked'].includes(from)) {
          showNotification({
            id,
            color: 'teal',
            withCloseButton: false,
            message: <CloneNotificationContent name={name} id={id} />,
          });
        }
        callback(from);
      })
      .finally(() => {
        setCloneLoading(false);
        handleModalClose();
      });
  };

  return (
    <Modal centered size="lg" withCloseButton={false} opened={!!cloneTarget} onClose={handleModalClose}>
      <CloneModalContent name={name} />
      <Group justify="flex-end" className="mt-3">
        <Button variant="outline" color="gray" onClick={handleModalClose}>
          {t('alert_cancel')}
        </Button>
        <Button
          loading={cloneLoading}
          onClick={() => {
            cloneDatasets();
          }}
        >
          {t('alert_ok')}
        </Button>
      </Group>
    </Modal>
  );
};
